import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import ThemeBand from "@/components/ThemeBand";
import { publicImageUrl } from "@/components/ImageUpload";

/**
 * Renders an illustrated theme band (top or bottom) for an invite.
 *
 * Strategy:
 *  - First try GET /api/theme-art — fast, returns cached paths if they exist.
 *  - If missing, POST /api/theme-art/ensure (which generates + caches ~30s).
 *  - While loading or on failure, fall back to the hand-drawn SVG <ThemeBand>.
 *
 * Every event with the same (theme, custom_theme, event_type) reuses the exact
 * same cached PNG — generation happens once, ever, per combination.
 */
export default function ThemeBandImage({ theme, customTheme, eventType, variant = "top", triggerGenerate = false }) {
  const [paths, setPaths] = useState({ top: null, bottom: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams({
      theme: theme || "",
      custom_theme: customTheme || "",
      event_type: eventType || "",
    }).toString();

    const run = async () => {
      try {
        const { data } = await api.get(`/theme-art?${params}`);
        if (cancelled) return;
        if (data?.top_path && data?.bottom_path) {
          setPaths({ top: data.top_path, bottom: data.bottom_path });
          setLoading(false);
          return;
        }
        if (!triggerGenerate) {
          setLoading(false);
          return;
        }
        // Generate-on-demand (only when caller asked — usually on invite creation/publish)
        const { data: gen } = await api.post("/theme-art/ensure", {
          theme: theme || "",
          custom_theme: customTheme || "",
          event_type: eventType || "",
        });
        if (cancelled) return;
        setPaths({ top: gen.top_path, bottom: gen.bottom_path });
      } catch {
        // Fail quietly — the SVG fallback will show.
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [theme, customTheme, eventType, triggerGenerate]);

  const storedPath = variant === "top" ? paths.top : paths.bottom;

  if (loading) {
    // Fall back to SVG band while generating so the page is never empty
    return <ThemeBand theme={theme} customTheme={customTheme} variant={variant} />;
  }
  if (!storedPath) {
    return <ThemeBand theme={theme} customTheme={customTheme} variant={variant} />;
  }
  return (
    <div data-testid={`theme-band-image-${variant}`} className="w-full">
      <img
        src={publicImageUrl(storedPath)}
        alt=""
        aria-hidden="true"
        className="w-full block"
        style={{
          height: "auto",
          aspectRatio: "3 / 1",
          objectFit: "cover",
          objectPosition: variant === "top" ? "center top" : "center bottom",
          mixBlendMode: "multiply",
        }}
      />
    </div>
  );
}
